import { GrowthBook } from '@growthbook/growthbook';
import { Mobile } from './mobile';
import QueryString from './querystring';
import { UUID } from './uuid';

declare interface myWindow extends Window {
    readonly growthbookAPIHost: string;
    readonly growthbookClientKey: string;
    readonly dataLayer?: Record<string, unknown>[];
}
declare const window: myWindow;

let promise: Promise<GrowthBook> | undefined;

export function AB() {
    if (typeof window.growthbookAPIHost === 'undefined' || typeof window.growthbookClientKey === 'undefined') {
        return Promise.reject(new Error('GrowthBook API Host and Client Key are required'));
    }

    if (typeof promise === 'undefined') {
        promise = new Promise(resolve => {
            const growthbook = new GrowthBook({
                apiHost: window.growthbookAPIHost,
                clientKey: window.growthbookClientKey,
                enableDevMode: true,
                trackingCallback: (experiment, result) => {
                    // eslint-disable-next-line no-console
                    console.log('Viewed Experiment', {
                        experimentId: experiment.key,
                        variationId: result.key,
                    });

                    window.dataLayer?.push({
                        'event': 'experiment_viewed',
                        'experiment_id': experiment.key,
                        'variation_id': result.key,
                    });
                },
            });

            // Wait for features to be available
            void growthbook.init({ streaming: true }).then(() => {
                let id = window.localStorage.getItem('growthbookId');
                if (!id) {
                    id = UUID();
                    window.localStorage.setItem('growthbookId', id);
                }

                let browser: 'chrome' | 'edge' | 'firefox' | 'safari' | 'unknown';
                if (navigator.userAgent.includes('Chrome')) {
                    browser = 'chrome';
                } else if (navigator.userAgent.includes('Edg')) {
                    browser = 'edge';
                } else if (navigator.userAgent.includes('Firefox')) {
                    browser = 'firefox';
                } else if (navigator.userAgent.includes('Safari')) {
                    browser = 'safari';
                } else {
                    browser = 'unknown';
                }

                void growthbook.setAttributes({
                    'id': id + (QueryString().get('ab') ?? ''),
                    'url': window.location.href,
                    'path': window.location.pathname,
                    'host': window.location.hostname,
                    'query': window.location.search,
                    'deviceType': Mobile() ? 'mobile' : 'desktop',
                    'browser': browser,
                    'utmSource': QueryString().get('utm_source'),
                    'utmMedium': QueryString().get('utm_medium'),
                    'utmCampaign': QueryString().get('utm_campaign'),
                    'utmTerm': QueryString().get('utm_term'),
                    'utmContent': QueryString().get('utm_content'),
                }).then(() => resolve(growthbook));
            });
        });
    }

    return promise;
}

function initAB() {
    // parent element -> feature name
    const found = new Map<Element, Set<string>>();
    document.querySelectorAll<HTMLElement>('[data-ab-feature-show]').forEach($el => {
        const $parent = $el.parentElement;
        if (!$parent) {
            return;
        }
        let parentFound = found.get($parent);
        if (!parentFound) {
            parentFound = new Set();
            found.set($parent, parentFound);
        }

        const feature = $el.getAttribute('data-ab-feature-show') ?? '';
        if (parentFound.has(feature)) {
            return;
        }

        const $els = Array.from($parent.children).filter($child => $child.getAttribute('data-ab-feature-show') === feature);
        if (!$els.length) {
            return;
        }

        void AB().then(growthbook => {
            const variant = growthbook.getFeatureValue(feature, '');
            let $match = Array.from($els).find($el => $el.getAttribute('data-ab-variant') === String(variant));
            if (!$match) {
                $match = $els[0];
            }
            // Hiding the losers lets a variant render visible-by-default, so an above-the-fold
            // block can hold its box (and be a discoverable LCP element) instead of popping in
            // and shifting the page once GrowthBook's network fetch resolves.
            $els.forEach($el => {
                if ($el !== $match) {
                    $el.classList.add('d-none');
                }
            });
            $match.classList.remove('d-none');
            if ($match instanceof HTMLElement && $match.style.display.trim().startsWith('none')) {
                $match.style.display = '';
            }
        });
    });

    document.querySelectorAll('[data-ab-feature-text]').forEach($el => {
        const feature = $el.getAttribute('data-ab-feature-text') ?? '';
        void AB().then(growthbook => {
            const value = growthbook.getFeatureValue(feature, '') || $el.getAttribute('data-ab-fallback') || '';
            $el.textContent = value;
        });
    });
}

initAB();
